import { useMemo, useState } from 'react';

import { Empty, Field, Search } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { useDebounced } from '../../hooks.js';
import { useSola } from '../../sola.jsx';
import { matchStudent } from '../../lib/progress.js';
import { today, tecajniki, vaje } from '../../lib/format.js';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Dan na terenu.

  Inštruktor se vrne z vzletišča in v enem koraku vpiše: kdo je bil tam,
  katere vaje so opravili in koliko letov je vsak naredil. Vse je potrjeno
  takoj — inštruktor je bil zraven.

  Zaslon je narejen za uporabo z eno roko na soncu: velike tarče, izbrani
  tečajniki so ves čas vidni kot značke, povzetek in gumb sta prilepljena
  na dno.
*/

export function FieldDay({ users, progress, onSaved }) {
  const { toast, confirm } = useUI();
  const { program } = useSola();

  const [day, setDay] = useState({ date: today(), vzletisce: '', note: '', leti: 0 });
  const [picked, setPicked] = useState([]);
  const [subs, setSubs] = useState([]);
  const [query, setQuery] = useState('');
  const [openEx, setOpenEx] = useState(null);
  const [busy, setBusy] = useState(false);

  const q = useDebounced(query, 180);

  const candidates = useMemo(
    () => users.filter((u) => !u.archived && !u.testni && u.ime).filter((u) => matchStudent(u, q)),
    [users, q],
  );

  const chosen = useMemo(() => users.filter((u) => picked.includes(u.id)), [users, picked]);
  const leti = Math.max(0, Math.min(20, parseInt(day.leti, 10) || 0));

  const toggleUser = (id) =>
    setPicked((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const toggleSub = (k) => setSubs((l) => (l.includes(k) ? l.filter((x) => x !== k) : [...l, k]));

  /** Koliko vrstic bo dejansko nastalo — brez tistih, ki so že potrjene. */
  const willCreate = useMemo(
    () =>
      api.buildFieldDayRows({ ids: picked, subKeys: subs, date: day.date, progressByUser: progress })
        .length,
    [picked, subs, day.date, progress],
  );

  const save = async () => {
    if (!picked.length) {
      toast.bad(t('Izberi vsaj enega tečajnika.'));
      return;
    }
    if (leti > 0 && !day.vzletisce.trim()) {
      toast.bad(t('Za vpis letov vpiši vzletišče.'));
      return;
    }
    if (!subs.length && !leti) {
      const ok = await confirm({
        title: t('Shranim samo prisotnost?'),
        body: t('Nisi izbral nobene vaje in nisi vpisal letov.'),
        confirmLabel: t('Shrani prisotnost'),
      });
      if (!ok) return;
    }

    setBusy(true);
    try {
      const res = await api.saveFieldDay({
        ids: picked,
        subKeys: subs,
        date: day.date || today(),
        vzletisce: day.vzletisce.trim(),
        note: day.note.trim(),
        leti,
        progressByUser: progress,
      });

      if (res.errs.length) {
        toast.bad(t('Delno shranjeno:') + ' ' + res.errs.join('; '), { duration: 12000 });
      } else {
        toast.ok(
          t('Shranjeno — {a} {wa}, {b} novih potrjenih {wb}, {c} letov.', {
            a: res.nStudents,
            wa: tecajniki(res.nStudents),
            b: res.nSubs,
            wb: vaje(res.nSubs),
            c: res.nFlights,
          }),
          { duration: 7000 },
        );
      }

      setSubs([]);
      setDay((d) => ({ ...d, leti: 0 }));
      await onSaved();
    } catch (e) {
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Dan in teren')}</span>
        </div>
        <div className="card__body grid-auto">
          <Field label={t('Datum')} id="fd-date">
            <input
              id="fd-date"
              type="date"
              className="input"
              value={day.date}
              onChange={(e) => setDay((d) => ({ ...d, date: e.target.value }))}
            />
          </Field>
          <Field label={t('Vzletišče')} id="fd-site">
            <input
              id="fd-site"
              className="input"
              placeholder={t('npr. Golte')}
              value={day.vzletisce}
              onChange={(e) => setDay((d) => ({ ...d, vzletisce: e.target.value }))}
            />
          </Field>
          <Field label={t('Leti na tečajnika')} hint={t('Največ 20')} id="fd-leti">
            <input
              id="fd-leti"
              type="number"
              min="0"
              max="20"
              className="input"
              value={day.leti}
              onChange={(e) => setDay((d) => ({ ...d, leti: e.target.value }))}
            />
          </Field>
          <Field label={t('Opomba dneva')} id="fd-note">
            <input
              id="fd-note"
              className="input"
              placeholder={t('npr. JZ 10–15 km/h, termika po 13h')}
              value={day.note}
              onChange={(e) => setDay((d) => ({ ...d, note: e.target.value }))}
            />
          </Field>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title spacer">{t('Kdo je na terenu')} ({picked.length})</span>
          {picked.length > 0 && (
            <button className="btn btn--sm btn--ghost" onClick={() => setPicked([])}>
              {t('Počisti')}
            </button>
          )}
        </div>

        {chosen.length > 0 && (
          <div className="row row--wrap" style={{ padding: 'var(--s-3) var(--s-4) 0', gap: 'var(--s-2)' }}>
            {chosen.map((u) => (
              <button
                key={u.id}
                className="badge"
                style={{ background: 'var(--brand-ink)', color: '#fff', padding: '5px 10px' }}
                onClick={() => toggleUser(u.id)}
                aria-label={t('Odstrani {name}', { name: `${u.ime} ${u.priimek}` })}
              >
                #{u.stevilka} {u.ime}
                <Icon name="x" size={12} />
              </button>
            ))}
          </div>
        )}

        <div className="row" style={{ padding: 'var(--s-3) var(--s-4)' }}>
          <Search value={query} onChange={setQuery} placeholder={t('Išči po številki ali imenu')} />
        </div>

        <div className="list" style={{ maxHeight: 300, overflowY: 'auto' }}>
          {candidates.map((u) => {
            const on = picked.includes(u.id);
            return (
              <label
                key={u.id}
                className="list__row"
                style={{ cursor: 'pointer', background: on ? 'var(--brand-wash)' : undefined }}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={on}
                  onChange={() => toggleUser(u.id)}
                />
                <span className="num t-sm t-black t-brand" style={{ minWidth: 32 }}>
                  #{u.stevilka}
                </span>
                <span className={`spacer t-md${on ? ' t-bold' : ''}`}>
                  {u.ime} {u.priimek}
                </span>
              </label>
            );
          })}
          {candidates.length === 0 && <Empty icon="search" title={t('Ni zadetkov')} />}
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title spacer">{t('Opravljene vaje')} ({subs.length})</span>
          {subs.length > 0 && (
            <button className="btn btn--sm btn--ghost" onClick={() => setSubs([])}>
              {t('Počisti')}
            </button>
          )}
        </div>

        {program.sklopi.map(({ key, label, list }) => (
          <div key={key}>
            <div className="section-label">{label}</div>
            {list.map((ex) => {
              const nSel = subs.filter((k) => k.startsWith(ex.id + ':')).length;
              const open = openEx === ex.id;
              return (
                <div key={ex.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <button
                    className="row list__row"
                    aria-expanded={open}
                    onClick={() => setOpenEx(open ? null : ex.id)}
                  >
                    <span className="spacer t-md t-bold" style={{ textAlign: 'left' }}>
                      {ex.title}
                    </span>
                    {nSel > 0 && <span className="badge badge--brand num">{nSel}</span>}
                    <Icon name={open ? 'up' : 'down'} size={18} className="t-dimmer" />
                  </button>

                  {open &&
                    ex.subs.map((sub, si) => {
                      const k = `${ex.id}:${si}`;
                      const on = subs.includes(k);
                      const already = picked.filter(
                        (uid) => progress[uid]?.[ex.id]?.subs?.[si]?.status === 'confirmed',
                      ).length;
                      return (
                        <label
                          key={k}
                          className="row"
                          style={{
                            padding: 'var(--s-2) var(--s-4) var(--s-2) var(--s-8)',
                            borderTop: '1px solid var(--border)',
                            background: on ? 'var(--brand-wash)' : 'var(--surface-2)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={on}
                            onChange={() => toggleSub(k)}
                          />
                          <span className="spacer t-md">{sub}</span>
                          {picked.length > 0 && already > 0 && (
                            <span
                              className="t-xs t-ok t-bold num"
                              title={t('Toliko izbranih tečajnikov ima to že potrjeno — tem se ne bo spremenilo')}
                            >
                              <Icon name="check" size={12} style={{ display: 'inline-block', verticalAlign: '-1px' }} />{' '}
                              {already}/{picked.length}
                            </span>
                          )}
                        </label>
                      );
                    })}
                </div>
              );
            })}
          </div>
        ))}
      </section>

      <div className="sticky-bar">
        <div className="spacer t-sm t-dim">
          <strong className="num">{picked.length}</strong> {tecajniki(picked.length)} ·{' '}
          <strong className="num">{willCreate}</strong> {t('novih potrjenih')} {vaje(willCreate)}
          {leti > 0 && (
            <>
              {' '}
              · <strong className="num">{leti * picked.length}</strong> {t('letov')}
            </>
          )}
        </div>
        <button className="btn btn--primary" onClick={save} disabled={busy || !picked.length}>
          {busy ? t('Shranjujem …') : t('Shrani dan')}
        </button>
      </div>
    </div>
  );
}
